// Meta (Facebook) Pixel event helper.
//
// The base pixel + PageView is injected at runtime by TagInjector, but only
// AFTER its /settings/public request resolves. Page-load events (ViewContent,
// InitiateCheckout) fire well before that, so `window.fbq` does not exist yet
// and firing directly would silently drop them. Everything below is therefore
// buffered until fbq appears, then flushed in order.
//
// If no pixel is configured, fbq never appears, the buffer is dropped at
// timeout, and every call was a harmless no-op.

const CURRENCY = 'INR';
const READY_TIMEOUT_MS = 15000; // give up buffering after this
const POLL_MS = 200;

let queue = [];
let polling = false;

const fbqReady = () => typeof window !== 'undefined' && typeof window.fbq === 'function';

function flush() {
  const pending = queue;
  queue = [];
  pending.forEach(({ event, data, opts }) => {
    try {
      if (opts) window.fbq('track', event, data, opts);
      else window.fbq('track', event, data);
    } catch { /* ignore */ }
  });
}

function startPolling() {
  if (polling) return;
  polling = true;
  const startedAt = Date.now();
  const timer = setInterval(() => {
    if (fbqReady()) {
      clearInterval(timer);
      polling = false;
      flush();
    } else if (Date.now() - startedAt > READY_TIMEOUT_MS) {
      clearInterval(timer);
      polling = false;
      queue = []; // no pixel configured — drop
    }
  }, POLL_MS);
}

// Fire a Meta standard event. Buffers until the pixel is ready.
// `opts` carries Meta's delivery options — notably { eventID }, which lets Meta
// collapse this browser event and the matching Conversions API event from our
// server into a single conversion instead of counting the sale twice.
export function trackPixel(event, data = {}, opts = null) {
  try {
    if (typeof window === 'undefined') return;
    if (fbqReady()) {
      if (opts) window.fbq('track', event, data, opts);
      else window.fbq('track', event, data);
      return;
    }
    queue.push({ event, data, opts });
    startPolling();
  } catch { /* never let analytics break the page */ }
}

// ── ViewContent — a product page was viewed ──────────────────────
export function trackViewContent({ id, name, price } = {}) {
  if (id == null) return;
  trackPixel('ViewContent', {
    content_ids: [String(id)],
    content_type: 'product',
    content_name: name,
    value: Number(price) || 0,
    currency: CURRENCY,
  });
}

// ── AddToCart — item(s) added to the cart ────────────────────────
export function trackAddToCart({ id, name, price, qty = 1 } = {}) {
  if (id == null) return;
  trackPixel('AddToCart', {
    content_ids: [String(id)],
    content_type: 'product',
    content_name: name,
    value: (Number(price) || 0) * (Number(qty) || 1),
    currency: CURRENCY,
  });
}

// ── InitiateCheckout — checkout page reached ─────────────────────
export function trackInitiateCheckout({ items = [], value = 0 } = {}) {
  trackPixel('InitiateCheckout', {
    content_ids: items.map(i => String(i)),
    content_type: 'product',
    num_items: items.length,
    value: Number(value) || 0,
    currency: CURRENCY,
  });
}

// ── Purchase — order placed successfully ─────────────────────────
// De-duped per order so a page refresh on the thank-you page never
// double-counts a sale in Meta.
export function trackPurchase({ orderKey, items = [], value = 0 } = {}) {
  try {
    if (orderKey) {
      const seenRaw = localStorage.getItem('yellove_fb_purchases') || '[]';
      const seen = JSON.parse(seenRaw);
      if (seen.includes(String(orderKey))) return; // already tracked
      seen.push(String(orderKey));
      localStorage.setItem('yellove_fb_purchases', JSON.stringify(seen.slice(-50)));
    }
  } catch { /* ignore storage errors, still fire below */ }

  trackPixel('Purchase', {
    content_ids: items.map(i => String(i)),
    content_type: 'product',
    num_items: items.length,
    value: Number(value) || 0,
    currency: CURRENCY,
  }, orderKey ? { eventID: `order_${orderKey}` } : null);
}

// SPA route change — Meta only auto-fires PageView when the base pixel loads, so
// every in-app navigation after that went unreported.
export function trackPageView() {
  trackPixel('PageView');
}
